import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@components';
import classNames from 'classnames';
import GlobalCtx from '@components/globalContext';

type ModalType = {
  buttonProps?: [any];
  title?: string;
  cssClass?: string;
  children: React.ReactElement;
};

type Props = React.PropsWithChildren<ModalType>;

const Modal = ({ buttonProps, children, cssClass, title }: Props): JSX.Element => {
  const { app, setApp } = React.useContext(GlobalCtx);
  const { isVisible: isVisibleViaContext } = app.modal;
  const [isVisible, setIsVisible] = React.useState<boolean>(isVisibleViaContext);

  const prevVisibleRef = useRef(isVisibleViaContext);
  const innerRef = React.createRef<HTMLDivElement>();

  const close = () => {
    setApp({
      ...app,
      modal: {
        ...app.modal,
        isVisible: false,
      },
    });
  };

  const onClickAnywhere = (e: any) => {
    const { current } = innerRef;
    if (current && current.contains(e.target)) {
      return;
    } else if (isVisible) {
      close();
    }
  };

  // isVisible controls visiblity, can be toggled via outside via isVisibleViaContext
  useEffect(() => {
    if (!prevVisibleRef.current && isVisible) {
      document.addEventListener('mousedown', onClickAnywhere);

      return () => {
        document.removeEventListener('mousedown', onClickAnywhere);
      };
    }

    // store previous state so we don't call onClose for initial value which is false
    prevVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    // Allow setting from outside
    if (isVisibleViaContext === isVisible) {
      return;
    }
    setIsVisible(isVisibleViaContext);
  }, [isVisibleViaContext]);

  if (!isVisible) return null;

  return ReactDOM.createPortal(
    <div className={classNames('modal', cssClass)}>
      <div className="modal-inner" style={{ minWidth: '400px', maxWidth: '900px' }} ref={innerRef}>
        <header className="modal-header">
          <section>{title && <h1 className="modal-title">{title}</h1>}</section>
          <a
            href="#"
            onClick={(e: any) => {
              e.preventDefault();
              close();
            }}
            title="Close"
            className="modal-close-btn z-10"
          >
            X
          </a>
        </header>
        <div className="modal-body">
          {children}
          {buttonProps && (
            <div className="modal__buttons">
              {buttonProps.map(({ buttonChildren, ...allButtonProps }) => (
                <Button key={uuidv4()} {...allButtonProps}>
                  {buttonChildren}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.getElementById('root'),
  );
};

export default Modal;
